import { useTimeout } from '@pogokid/react-hooks'
import {
  Box,
  CircularProgress,
  Fade,
  LinearProgress,
} from '@pogokid/mui'
import { Logo } from '~m/AppUi/components/Logo.tsx'
import { ReactNode, useEffect } from 'react'
import { captureException } from '@pogokid/log'
import { Text } from '~m/designSystem'

interface AppLoadingOrErrorProps {
  message?: ReactNode
  error?: Error | null
  /**
   * Determinate progress as a fraction (0–1). When set, a
   * progress bar is shown instead of the indeterminate spinner.
   */
  progress?: number | null
  /**
   * Something the user can do about the error, e.g. a retry
   * button. Only shown with an error.
   */
  action?: ReactNode
  /**
   * Send the error to Sentry. Turn off for errors we expect and
   * already recover from. Defaults to true.
   */
  reportError?: boolean
}

export function AppLoadingOrError({
  message,
  error,
  progress,
  action,
  reportError = true,
}: AppLoadingOrErrorProps) {
  useEffect(() => {
    if (error && reportError) {
      console.error(error)
      captureException(error)
    }
  }, [error, reportError])

  const [fadeInLogo] = useTimeout(500)
  const [fadeInProgress] = useTimeout(3000)

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      height="100vh"
      padding={1}
      gap={2}
      textAlign="center"
    >
      <Fade in={!!fadeInLogo()} timeout={3000}>
        <Box data-testid="LoadingOrError">
          <Logo />
          {message && (
            <Text variant="section-title">{message}</Text>
          )}
        </Box>
      </Fade>
      {typeof progress === 'number' ? (
        <Fade in={!!fadeInLogo()} timeout={1000}>
          <Box width="min(280px, 80vw)">
            <LinearProgress
              variant="determinate"
              value={Math.min(1, Math.max(0, progress)) * 100}
            />
          </Box>
        </Fade>
      ) : error ? (
        <>
          <Text variant="body">Error: {error.message}</Text>
          {action}
        </>
      ) : (
        <Fade in={!!fadeInProgress()} timeout={3000}>
          <CircularProgress size={20} variant="indeterminate" />
        </Fade>
      )}
    </Box>
  )
}
